import { Calendar, Info, BarChart3, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { usePayroll } from '@/context/AppContext';
import { formatDate, formatDateShort, fmtNum } from '@/lib/payroll';

export function PeriodPage() {
  const { payrollData, period, rawById } = usePayroll();

  if (!payrollData) {
    return (
      <div className="space-y-5 animate-slide-up">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <Calendar size={22} className="text-indigo-500" />
          فترة التقرير
        </h2>
        <Card className="text-center !p-12">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center mb-3">
            <Info size={32} className="text-slate-400" />
          </div>
          <p className="text-slate-600 dark:text-slate-300">
            ارفع الملفات واحسب المرتبات أولاً لتحديد الفترة تلقائياً
          </p>
        </Card>
      </div>
    );
  }

  const totalRecords = Object.values(rawById).reduce((s, days) => s + days.length, 0);
  const employeesWithAbsence = Object.values(rawById).filter((d) => d.some((x: any) => x.isAbsent)).length;
  const otHours = payrollData.reduce((s, d) => s + d['إجمالي ساعات الإضافي'], 0);

  return (
    <div className="space-y-5 animate-slide-up">
      <h2 className="text-2xl font-extrabold flex items-center gap-2">
        <Calendar size={22} className="text-indigo-500" />
        فترة التقرير
      </h2>

      <Card accent="emerald" padding="lg">
        <h3 className="font-bold text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
          <Badge variant="emerald">✓ تلقائي</Badge>
          تم استخراج الفترة من ملف البصمة
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          الفترة المستخرجة بناءً على عمود Date في الملف المرفق
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-100 dark:border-emerald-800/40">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold">من تاريخ</div>
            <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
              {formatDate(period.start) || 'غير محدد'}
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{period.start}</div>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 border border-indigo-100 dark:border-indigo-800/40">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 font-semibold">إلى تاريخ</div>
            <div className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
              {formatDate(period.end) || 'غير محدد'}
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{period.end}</div>
          </div>
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-bold mb-3 text-base flex items-center gap-2">
          <BarChart3 size={18} className="text-indigo-500" /> إحصائيات الفترة
        </h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <StatTile label="إجمالي السجلات" value={totalRecords} color="indigo" icon={Calendar} />
          <StatTile label="موظف نشط" value={payrollData.length} color="emerald" icon={Info} />
          <StatTile label="فيهم غياب" value={employeesWithAbsence} color="rose" icon={AlertTriangle} />
          <StatTile label="يوم إضافي" value={(otHours / 8).toFixed(2)} color="amber" icon={BarChart3} />
        </div>
      </Card>
    </div>
  );
}

function StatTile({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: any;
  color: 'indigo' | 'emerald' | 'rose' | 'amber';
  icon: any;
}) {
  const colors = {
    indigo: 'from-indigo-500 to-violet-500',
    emerald: 'from-emerald-500 to-teal-500',
    rose: 'from-rose-500 to-pink-500',
    amber: 'from-amber-500 to-orange-500',
  };
  return (
    <div className={`text-center p-4 rounded-xl bg-gradient-to-br ${colors[color]} text-white shadow-lg`}>
      <Icon size={20} className="mx-auto mb-1 opacity-80" />
      <div className="text-2xl font-black">{value}</div>
      <div className="text-xs mt-1 opacity-90 font-medium">{label}</div>
    </div>
  );
}
